import { useState, useEffect } from "react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts"
import { FileText, CheckCircle, DollarSign, Wrench, TrendingDown, PiggyBank } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { PageHeader } from "@/components/PageHeader"
import { supabase } from "@/integrations/supabase/client"
import { criteriosData } from "@/lib/criteriosData"

const PERIODOS = [
  { label: "7 dias", days: 7 },
  { label: "30 dias", days: 30 },
  { label: "90 dias", days: 90 },
  { label: "Este ano", days: 365 },
  { label: "Todo período", days: "all" as const },
]

const STATUS_COLORS: Record<string, string> = {
  pendente: "#94a3b8",
  ativa: "#3b82f6",
  concluida: "#22c55e",
  cancelada: "#ef4444",
}

const STATUS_LABELS: Record<string, string> = {
  pendente: "Pendente",
  ativa: "Em Andamento",
  concluida: "Concluída",
  cancelada: "Cancelada",
}

// Mapeia prefixo do criterio_id para categoria
const categoriaMap: Record<string, string> = {}
criteriosData.forEach(c => {
  const prefixo = c.id.replace(/_\d+$/, '')
  categoriaMap[prefixo] = c.categoria
})

function getCategoriaFromId(criterioId: string): string {
  const prefixo = criterioId.replace(/_\d+$/, '')
  return categoriaMap[prefixo] ?? criterioId
}

function formatBRL(valor: number) {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

interface CategoriaReparo {
  categoria: string
  total: number
}

interface ReparoRecorrente {
  nome: string
  quantidade: number
  total: number
}

export default function Relatorios() {
  const [periodo, setPeriodo] = useState<number | "all">(30)
  const [loading, setLoading] = useState(true)

  // Avaliações
  const [totalAvaliacoes, setTotalAvaliacoes] = useState(0)
  const [concluidas, setConcluidas] = useState(0)
  const [statusData, setStatusData] = useState<{ name: string; value: number; color: string }[]>([])
  const [mesData, setMesData] = useState<{ mes: string; avaliacoes: number }[]>([])

  // Reparos
  const [totalReparos, setTotalReparos] = useState(0)
  const [reparoMedio, setReparoMedio] = useState(0)
  const [economiaTotalFipe, setEconomiaTotalFipe] = useState(0)
  const [economiaMedia, setEconomiaMedia] = useState(0)
  const [maiorEconomia, setMaiorEconomia] = useState(0)
  const [propostasValidas, setPropostasValidas] = useState(0)
  const [categoriaReparos, setCategoriaReparos] = useState<CategoriaReparo[]>([])
  const [reparosRecorrentes, setReparosRecorrentes] = useState<ReparoRecorrente[]>([])

  useEffect(() => { fetchRelatorios() }, [periodo])

  async function fetchRelatorios() {
    setLoading(true)
    const desde = new Date()
    if (periodo !== "all") {
      desde.setDate(desde.getDate() - periodo)
    }
    const desdeISO = desde.toISOString()

    const avaliacoesQuery = supabase
      .from('avaliacoes')
      .select('id, status, nota_final, created_at')

    const propostasQuery = supabase
      .from('propostas')
      .select('valor_fipe, custo_reparos, valor_ofertado, created_at')

    const [avalRes, propostaRes, criteriosRes] = await Promise.all([
      (periodo === "all" ? avaliacoesQuery : avaliacoesQuery.gte('created_at', desdeISO)),
      (periodo === "all" ? propostasQuery : propostasQuery.gte('created_at', desdeISO)),
      supabase
        .from('avaliacoes_criterios')
        .select('criterio_id, custo_reparo, avaliacao_id, status, aprovado')
        .gt('custo_reparo', 0),
    ])

    const avaliacoes = avalRes.data ?? []
    const propostas = propostaRes.data ?? []
    const criterios = criteriosRes.data ?? []

    // Filtra critérios pelo período (usando IDs das avaliações do período)
    const avalIds = new Set(avaliacoes.map(a => a.id))
    const criteriosPeriodo = criterios.filter((c) => {
      if (!avalIds.has(c.avaliacao_id)) return false
      return c.status ? c.status === 'reprovado' : c.aprovado === false
    })

    // — KPIs avaliações
    const concluidasArr = avaliacoes.filter(a => a.status === 'concluida')
    setTotalAvaliacoes(avaliacoes.length)
    setConcluidas(concluidasArr.length)

    // — KPIs reparos
    const totalRep = criteriosPeriodo.reduce((s, c) => s + Number(c.custo_reparo), 0)
    const avalComReparo = new Set(criteriosPeriodo.map(c => c.avaliacao_id)).size
    setTotalReparos(totalRep)
    setReparoMedio(avalComReparo > 0 ? totalRep / avalComReparo : 0)

    // — Economia (FIPE - oferta)
    const economias = propostas
      .filter(p => p.valor_fipe > 0 && p.valor_ofertado > 0)
      .map(p => p.valor_fipe - p.valor_ofertado)
    setPropostasValidas(economias.length)
    const econTotal = economias.reduce((a, b) => a + b, 0)
    setEconomiaTotalFipe(econTotal)
    setEconomiaMedia(economias.length > 0 ? econTotal / economias.length : 0)
    setMaiorEconomia(economias.length > 0 ? Math.max(...economias) : 0)

    // — Reparos por categoria
    const porCategoria: Record<string, number> = {}
    const porReparo: Record<string, { nome: string; quantidade: number; total: number }> = {}
    criteriosPeriodo.forEach(c => {
      const cat = getCategoriaFromId(c.criterio_id)
      porCategoria[cat] = (porCategoria[cat] ?? 0) + Number(c.custo_reparo)

      const criterioBase = criteriosData.find(item => item.id === c.criterio_id.trim().toLowerCase())
      const nome = criterioBase?.nome || c.criterio_id
      const atual = porReparo[c.criterio_id] ?? { nome, quantidade: 0, total: 0 }
      atual.quantidade += 1
      atual.total += Number(c.custo_reparo)
      porReparo[c.criterio_id] = atual
    })
    setCategoriaReparos(
      Object.entries(porCategoria)
        .map(([categoria, total]) => ({ categoria, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8)
    )
    setReparosRecorrentes(
      Object.values(porReparo)
        .sort((a, b) => {
          if (b.quantidade !== a.quantidade) return b.quantidade - a.quantidade
          return b.total - a.total
        })
        .slice(0, 5)
    )

    // — Status pie
    const statusCount: Record<string, number> = {}
    avaliacoes.forEach(a => { statusCount[a.status] = (statusCount[a.status] ?? 0) + 1 })
    setStatusData(
      Object.entries(statusCount).map(([status, value]) => ({
        name: STATUS_LABELS[status] ?? status,
        value,
        color: STATUS_COLORS[status] ?? '#94a3b8',
      }))
    )

    // — Por mês
    const porMes: Record<string, number> = {}
    avaliacoes.forEach(a => {
      const mes = new Date(a.created_at).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
      porMes[mes] = (porMes[mes] ?? 0) + 1
    })
    setMesData(
      Object.entries(porMes)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mes, avaliacoes]) => ({ mes, avaliacoes }))
    )

    setLoading(false)
  }

  const semDados = !loading && totalAvaliacoes === 0
  const basePropostasLabel = propostasValidas === 1
    ? "Base do período: 1 fechamento"
    : propostasValidas > 1
      ? `Base do período: ${propostasValidas} fechamentos`
      : "Sem fechamentos válidos no período"

  const kpiAvaliacoes = [
    { title: "Total de Avaliações", value: loading ? "..." : totalAvaliacoes.toString(), icon: FileText, color: "text-primary", bg: "bg-primary/10" },
    { title: "Concluídas", value: loading ? "..." : concluidas.toString(), icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
  ]

  const kpiReparos = [
    { title: "Economia Total", value: loading ? "..." : propostasValidas > 0 ? formatBRL(economiaTotalFipe) : "Sem fechamentos válidos", icon: PiggyBank, color: "text-emerald-600", bg: "bg-emerald-50", desc: propostasValidas > 0 ? basePropostasLabel : "FIPE menos valor ofertado" },
    { title: "Economia Média", value: loading ? "..." : propostasValidas > 0 ? formatBRL(economiaMedia) : "Sem base no período", icon: DollarSign, color: "text-teal-600", bg: "bg-teal-50", desc: propostasValidas > 0 ? basePropostasLabel : "Por veículo negociado" },
    { title: "Reparo Médio/Veículo", value: loading ? "..." : formatBRL(reparoMedio), icon: TrendingDown, color: "text-red-600", bg: "bg-red-50", desc: "Custo médio por avaliação" },
    { title: "Total em Reparos", value: loading ? "..." : formatBRL(totalReparos), icon: Wrench, color: "text-orange-600", bg: "bg-orange-50", desc: "Soma dos custos identificados" },
  ]

  return (
    <div className="space-y-8">
      <PageHeader title="Relatórios" description="Indicadores e desempenho do sistema">
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
          {PERIODOS.map(p => (
            <Button key={p.days} variant={periodo === p.days ? "default" : "outline"} size="sm" onClick={() => setPeriodo(p.days)}>
              {p.label}
            </Button>
          ))}
        </div>
      </PageHeader>

      {/* KPIs — Financeiro */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Impacto Financeiro</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {kpiReparos.map(card => (
            <Card key={card.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 px-3.5 pb-2 pt-3.5 sm:px-6 sm:pt-6">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">{card.title}</CardTitle>
                <div className={`rounded-full p-1.5 sm:p-2 ${card.bg}`}>
                  <card.icon className={`h-4 w-4 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent className="px-3.5 pb-3.5 pt-0 sm:px-6 sm:pb-6">
                <div className="text-[1.75rem] font-bold leading-none sm:text-2xl">{card.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{card.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Ritmo da Operação</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-4">
          {kpiAvaliacoes.map(card => (
            <Card key={card.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 px-3.5 pb-2 pt-3.5 sm:px-6 sm:pt-6">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">{card.title}</CardTitle>
                <div className={`rounded-full p-1.5 sm:p-2 ${card.bg}`}>
                  <card.icon className={`h-4 w-4 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent className="px-3.5 pb-3.5 pt-0 sm:px-6 sm:pb-6">
                <div className="text-[1.75rem] font-bold leading-none sm:text-2xl">{card.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {semDados ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma avaliação no período selecionado.</p>
            <p className="text-sm text-muted-foreground mt-1">Realize avaliações para ver os relatórios aqui.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:gap-6 lg:grid-cols-3">
          {/* Avaliações por mês */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Avaliações por Mês</CardTitle>
              <CardDescription>Volume de avaliações criadas no período</CardDescription>
            </CardHeader>
            <CardContent className="px-3.5 pb-4 sm:px-6 sm:pb-6">
              {mesData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-center text-muted-foreground text-sm">Pouco volume no período para uma tendência confiável.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={mesData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "Avaliações"]} />
                    <Bar dataKey="avaliacoes" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Status pie */}
          <Card>
            <CardHeader>
              <CardTitle>Por Status</CardTitle>
              <CardDescription>Leitura operacional das avaliações no período</CardDescription>
            </CardHeader>
            <CardContent className="px-3.5 pb-4 sm:px-6 sm:pb-6">
              {statusData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-center text-muted-foreground text-sm">Sem avaliações suficientes para distribuição por status.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="45%" innerRadius={50} outerRadius={80} dataKey="value">
                      {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Reparos por Categoria</CardTitle>
              <CardDescription>Custo total identificado por área do veículo — quanto cada categoria pesa na negociação</CardDescription>
            </CardHeader>
            <CardContent className="px-3.5 pb-4 sm:px-6 sm:pb-6">
              {categoriaReparos.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                  Nenhum custo de reparo registrado no período.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, categoriaReparos.length * 44)}>
                  <BarChart
                    data={categoriaReparos}
                    layout="vertical"
                    margin={{ top: 4, right: 80, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11 }}
                      tickFormatter={v => formatBRL(v)}
                    />
                    <YAxis type="category" dataKey="categoria" tick={{ fontSize: 12 }} width={130} />
                    <Tooltip formatter={(v: number) => [formatBRL(v), "Custo total"]} />
                    <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]}
                      label={{ position: 'right', formatter: (v: number) => formatBRL(v), fontSize: 11 }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 5 Reparos Recorrentes</CardTitle>
              <CardDescription>Os itens que mais se repetem e mais afetam a oferta</CardDescription>
            </CardHeader>
            <CardContent className="px-3.5 pb-4 sm:px-6 sm:pb-6">
              {reparosRecorrentes.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-center text-muted-foreground text-sm">
                  Nenhum reparo recorrente com custo registrado no período.
                </div>
              ) : (
                <div className="space-y-3">
                  {reparosRecorrentes.map((reparo) => (
                    <div key={reparo.nome} className="rounded-xl border bg-muted/20 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{reparo.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            {reparo.quantidade} ocorrência{reparo.quantidade > 1 ? "s" : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-foreground">{formatBRL(reparo.total)}</p>
                          <p className="text-xs text-muted-foreground">custo acumulado</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Maior Impacto do Período</CardTitle>
              <CardDescription>Maior economia individual gerada entre FIPE e valor ofertado</CardDescription>
            </CardHeader>
            <CardContent className="px-3.5 pb-4 pt-0 sm:px-6 sm:pb-6">
              <div className="rounded-2xl border bg-gradient-to-r from-emerald-50 via-background to-background p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Maior economia individual</p>
                <p className="mt-3 text-3xl font-bold text-emerald-700">
                  {loading ? "..." : propostasValidas > 0 ? formatBRL(maiorEconomia) : "Sem fechamentos válidos"}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {propostasValidas > 0
                    ? `${basePropostasLabel}. Maior diferença identificada entre valor FIPE e valor ofertado no período selecionado.`
                    : "Quando houver fechamentos com FIPE e oferta válidos, o maior impacto aparecerá aqui."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
